import React, { useMemo, useState } from 'react';

import dompurify from 'dompurify';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { ButtonSelect, CheckBoxGroup, Form, Input, SingleSelect, spacing } from '@sine/sine-ui-components';

import { FieldType } from '../../../services/form/constants';
import { useJoiForm } from '../../../shared/customHooks';
import generateSiteCheckInFormSchema from '../schemas/siteCheckInFormSchemas';

const marginBottom = css`
  &:not(:last-child) {
    margin-bottom: ${spacing(2)};
  }
`;

const StyledImg = styled.img`
  max-width: 600px;
  ${marginBottom}
`;

const StyledDiv = styled.div`
  ${marginBottom}
`;

const booleanSwitchOptions = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

function generateInitialFormValues(fields = [], prefilledForm) {
  // check if there is a prefilledForm and use it as initial values

  let prefilledFormReduced = {};

  if (prefilledForm) {
    const convertArrayToObject = (array, key) => {
      const initialValue = {};
      return array.reduce((obj, item) => {
        return {
          ...obj,
          [item[key]]: item,
        };
      }, initialValue);
    };

    prefilledFormReduced = prefilledForm ? convertArrayToObject(prefilledForm, 'id') : undefined;
  }

  return fields
    .filter((field) => Object.values(FieldType).includes(field.type))
    .reduce((prev, curr) => {
      if (curr.type === 'multiplechoice') {
        return {
          ...prev,
          [curr.id]: prefilledFormReduced[curr.id]
            ? {
                label: prefilledFormReduced[curr.id]?.value,
                value: curr.options.findIndex((v) => v.includes(prefilledFormReduced[curr.id]?.value)),
              }
            : undefined || '',
        };
      } else if (curr.type === 'selectiveChoice') {
        return {
          ...prev,
          [curr.id]: prefilledFormReduced[curr.id] ? prefilledFormReduced[curr.id]?.value : undefined,
        };
      } else if (curr.type === 'booleanSwitch') {
        return { ...prev, [curr.id]: prefilledFormReduced[curr.id] ? prefilledFormReduced[curr.id]?.value : undefined };
      } else if (curr.type === 'text' || curr.type === 'number') {
        return { ...prev, [curr.id]: prefilledFormReduced[curr.id] ? prefilledFormReduced[curr.id]?.value : '' };
      } else if (curr.type === 'signature') {
        return { ...prev, [curr.id]: prefilledFormReduced[curr.id] ? prefilledFormReduced[curr.id]?.value : undefined };
      } else {
        return { ...prev };
      }
    }, {});
}

export default function SiteCheckInForm({ formRef, siteRequirements, visitorTypeId, prefilledForm }) {
  const formFields = siteRequirements?.form?.fields;
  const [selectiveChoiceValues, setSelectiveChoiceValues] = useState([]);

  const { schema, initialValues } = useMemo(
    () => ({
      schema: generateSiteCheckInFormSchema(formFields, selectiveChoiceValues),
      initialValues: generateInitialFormValues(formFields, prefilledForm?.responses),
    }),
    [siteRequirements, prefilledForm, selectiveChoiceValues],
  );

  formRef.current = useJoiForm(schema, initialValues);

  const checkInForm = formRef.current;

  return (
    <Form
      formSpacing='compact'
      values={checkInForm.values}
      errors={checkInForm.errors}
      onFieldChange={(name, value) => checkInForm.onChange(name, value, false)}
      onFieldBlur={checkInForm.onBlur}
    >
      {formFields.map(({ id, type, label, required, visitorTypes, options }) => {
        const includesVisitorType = visitorTypes.includes(visitorTypeId);

        if (!includesVisitorType) {
          return null;
        } else {
          switch (type) {
            case FieldType.TEXT:
              return (
                <Input key={id} name={id} label={`${label}${required ? ' *' : ''}`} data-st-ext={`input-text-${id}`} />
              );
            case FieldType.NUMBER:
              return (
                <Input
                  key={id}
                  name={id}
                  label={`${label}${required ? ' *' : ''}`}
                  data-st-ext={`input-number-${id}`}
                />
              );
            case FieldType.BOOLEAN_SWITCH:
              return (
                <ButtonSelect
                  key={id}
                  name={id}
                  label={`${label}${required ? ' *' : ''}`}
                  options={booleanSwitchOptions}
                  onChange={(value) => checkInForm.onChange(id, value)}
                  data-st-ext={`input-boolean-switch-${id}`}
                />
              );
            case FieldType.MULTIPLE_CHOICE:
              return (
                <SingleSelect
                  key={id}
                  name={id}
                  label={`${label}${required ? ' *' : ''}`}
                  options={options.map((option, index) => ({ label: option, value: index }))}
                  onChange={(value) => {
                    checkInForm.onChange(id, value, true);
                  }}
                  isSearchable={false}
                  data-st-ext={`input-multiple-choice-${id}`}
                  isClearable
                />
              );
            case FieldType.SELECTIVE_CHOICE:
              return (
                <CheckBoxGroup
                  key={id}
                  name={id}
                  label={`${label}${required ? ' *' : ''}`}
                  formSpacing='compact'
                  options={options.map((option) => ({ key: option, label: option }))}
                  data-st-ext={`input-selective-choice-${id}`}
                  onChange={(value) => {
                    setSelectiveChoiceValues(value);
                    checkInForm.onChange(id, value, true);
                  }}
                />
              );
            case FieldType.INFO:
              return (
                // We can ensure that this is from the server side
                // for anyone does not know about it in Code Review:
                // https://reactjs.org/docs/dom-elements.html#dangerouslysetinnerhtml
                <StyledDiv
                  key={id}
                  dangerouslySetInnerHTML={{ __html: dompurify.sanitize(label) }}
                  data-st-ext={`info-${id}`}
                />
              );
            case FieldType.IMAGE:
              return <StyledImg key={id} src={label} alt={label} data-st-ext={`image-${id}`} />;
            case FieldType.NDA:
              return (
                <StyledDiv
                  key={id}
                  dangerouslySetInnerHTML={{ __html: dompurify.sanitize(label) }}
                  data-st-ext={`nda-${id}`}
                />
              );
          }
        }
      })}
    </Form>
  );
}

SiteCheckInForm.propTypes = {
  formRef: PropTypes.object.isRequired,
  siteRequirements: PropTypes.object.isRequired,
  visitorTypeId: PropTypes.string.isRequired,
  prefilledForm: PropTypes.object,
};
